import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Shield, Send, Check, Calculator, Clock, Download, Smartphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useLogo } from '../context/LogoContext';
import LanguageSelector from '../components/LanguageSelector';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LandingPage() {
  const { t } = useLanguage();
  const { logoUrl } = useLogo();
  const [fee, setFee] = useState(null);
  const [flatFee, setFlatFee] = useState(500);
  const [displayAmount, setDisplayAmount] = useState(100000);
  const [footerText, setFooterText] = useState('');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/exchange/rate`).then(res => {
      setFee(prev => ({ ...prev, ...res.data }));
    }).catch(() => {});
    axios.get(`${API_URL}/api/public/fee-info`).then(res => {
      setFlatFee(res.data.flat_fee_jpy ?? 500);
      const amt = res.data.landing_display_amount || 100000;
      setDisplayAmount(amt);
      axios.get(`${API_URL}/api/exchange/calculate?amount=${amt}`).then(calcRes => {
        setFee(prev => ({ ...prev, ...calcRes.data }));
      }).catch(() => {});
    }).catch(() => {});
    axios.get(`${API_URL}/api/public/footer`).then(res => {
      setFooterText(res.data.text || '');
    }).catch(() => {});

    // PWA install detection
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }
    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(ios);

    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = useCallback(async () => {
    if (isIOS) { setShowIOSGuide(true); return; }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') setIsInstalled(true);
    setDeferredPrompt(null);
  }, [isIOS, deferredPrompt]);

  const feeJpy = flatFee;

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="border-b border-border/50 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="font-logo text-2xl font-bold text-primary flex items-center gap-2" data-testid="logo">
              <img src={logoUrl} alt="PRC Remit" className="h-9 w-9 rounded-full object-cover" />
              PRC Remit
            </div>
            <div className="flex items-center gap-4">
              <LanguageSelector />
              <Link to="/login">
                <Button variant="outline" className="rounded-full" data-testid="login-button">{t('landing_login')}</Button>
              </Link>
              <Link to="/register">
                <Button className="rounded-full" data-testid="register-button">{t('register')}</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-20 md:py-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-tight" data-testid="hero-title">
                {t('landing_hero_title_1')}
                <br />
                <span className="text-accent">{t('landing_hero_title_2')}</span>
                <br />{t('landing_hero_title_3')}
              </h1>
              <p className="text-lg text-muted-foreground mt-6 max-w-lg">
                {t('landing_hero_desc')}
              </p>
              <div className="flex flex-wrap gap-4 mt-8">
                <Link to="/register">
                  <Button size="lg" className="rounded-full h-14 px-8 text-lg" data-testid="hero-cta-button">
                    {t('landing_cta')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/calculator">
                  <Button variant="outline" size="lg" className="rounded-full h-14 px-8 text-lg" data-testid="calculator-button">
                    <Calculator className="mr-2 h-5 w-5" />
                    {t('landing_calculator')}
                  </Button>
                </Link>
                {!isInstalled && (
                  <Button variant="outline" size="lg" className="rounded-full h-14 px-8 text-lg border-accent text-accent hover:bg-accent hover:text-white" onClick={handleInstallClick} data-testid="hero-install-button">
                    <Download className="mr-2 h-5 w-5" />
                    {t('pwa_install_btn')}
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-8 mt-10">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary font-mono">¥{feeJpy.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">{t('landing_fee_label')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary font-mono">24h</div>
                  <div className="text-xs text-muted-foreground">{t('landing_arrival')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary font-mono">3</div>
                  <div className="text-xs text-muted-foreground">{t('landing_click_send')}</div>
                </div>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="glass rounded-3xl p-8 shadow-2xl">
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white mb-4">
                  <div className="text-sm text-slate-400 mb-2">JPY → BRL</div>
                  <div className="text-3xl font-bold font-mono">{fee?.final_rate ? fee.final_rate.toFixed(5) : '0.03200'}</div>
                </div>
                <div className="space-y-3 pb-16">
                  <div className="flex justify-between py-2">
                    <span className="text-sm text-muted-foreground">{t('landing_fee_label')}</span>
                    <span className="font-mono font-bold text-primary text-lg">¥{feeJpy.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-sm text-muted-foreground">{t('landing_exchange_rate')}</span>
                    <span className="font-mono font-bold text-primary text-lg">{fee?.markup ? (fee.markup * 100).toFixed(1) : '3.0'}%</span>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-8 -right-6 glass rounded-2xl p-5 shadow-xl max-w-[200px] z-10">
                <div className="text-sm text-muted-foreground mb-1">{t('landing_send_amount')}</div>
                <div className="text-3xl font-bold font-mono text-primary mb-1">¥{displayAmount.toLocaleString()}</div>
                <div className="text-sm text-accent font-medium">→ R$ {fee?.amount_brl ? fee.amount_brl.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '---'}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-primary mb-4">{t('landing_why_title')}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('landing_why_subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-card border border-border/50 rounded-2xl p-8 shadow-sm hover:shadow-md smooth-transition" data-testid="feature-card-1">
              <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mb-6"><Zap className="h-7 w-7 text-accent" /></div>
              <h3 className="font-heading text-xl font-semibold text-primary mb-3">{t('landing_feature_fast')}</h3>
              <p className="text-muted-foreground leading-relaxed">{t('landing_feature_fast_desc')}</p>
            </div>
            <div className="bg-card border border-border/50 rounded-2xl p-8 shadow-sm hover:shadow-md smooth-transition" data-testid="feature-card-2">
              <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mb-6"><Shield className="h-7 w-7 text-accent" /></div>
              <h3 className="font-heading text-xl font-semibold text-primary mb-3">{t('landing_feature_secure')}</h3>
              <p className="text-muted-foreground leading-relaxed">{t('landing_feature_secure_desc')}</p>
            </div>
            <div className="bg-card border border-border/50 rounded-2xl p-8 shadow-sm hover:shadow-md smooth-transition" data-testid="feature-card-3">
              <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mb-6"><Send className="h-7 w-7 text-accent" /></div>
              <h3 className="font-heading text-xl font-semibold text-primary mb-3">{t('landing_feature_cheap')}</h3>
              <p className="text-muted-foreground leading-relaxed">{(t('landing_feature_cheap_desc') || '').replace('{fee}', feeJpy.toLocaleString())}</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="how-it-works" className="py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-primary mb-4">{t('landing_how_title')}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('landing_how_subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: 1, title: t('landing_step1_title'), desc: t('landing_step1_desc') },
              { step: 2, title: t('landing_step2_title'), desc: t('landing_step2_desc') },
              { step: 3, title: t('landing_step3_title'), desc: t('landing_step3_desc') },
            ].map(({ step, title, desc }) => (
              <div key={step} className="text-center">
                <div className="w-16 h-16 rounded-full bg-accent text-white font-bold text-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">{step}</div>
                <h3 className="font-heading text-xl font-semibold text-primary mb-3">{title}</h3>
                <p className="text-muted-foreground leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-primary mb-4">{t('landing_pricing_title')}</h2>
            <p className="text-lg text-muted-foreground">{t('landing_pricing_subtitle')}</p>
          </div>
          <div className="bg-card border border-border/50 rounded-2xl p-8 md:p-12 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <div className="text-sm text-muted-foreground mb-2">{t('landing_fee_label')}</div>
                <div className="text-4xl font-bold font-heading text-primary">¥{feeJpy.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground mt-1">{t('landing_per_transfer')}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-2">{t('landing_exchange_rate')}</div>
                <div className="text-4xl font-bold font-heading text-primary">{t('landing_market_rate')}</div>
                <div className="text-sm text-muted-foreground mt-1">{(t('landing_markup_about') || '').replace('{rate}', fee?.markup ? (fee.markup * 100).toFixed(1) : '3.0')}</div>
              </div>
            </div>
            <div className="space-y-3 pt-6 border-t border-border">
              <div className="flex items-start gap-3"><Check className="h-5 w-5 text-accent mt-0.5" /><span className="text-muted-foreground">{t('landing_no_hidden')}</span></div>
              <div className="flex items-start gap-3"><Check className="h-5 w-5 text-accent mt-0.5" /><span className="text-muted-foreground">{t('landing_pix_bank')}</span></div>
              <div className="flex items-start gap-3"><Check className="h-5 w-5 text-accent mt-0.5" /><span className="text-muted-foreground">{t('landing_24h_support')}</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-primary mb-6">{t('landing_cta_title')}</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">{t('landing_cta_desc')}</p>
          <Link to="/register">
            <Button size="lg" className="rounded-full h-14 px-12 text-lg" data-testid="footer-cta-button">
              {t('landing_cta_button')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* iOS Install Guide Modal */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={() => setShowIOSGuide(false)}>
          <div className="bg-card rounded-t-2xl sm:rounded-2xl p-6 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()} data-testid="ios-guide-modal">
            <div className="text-center">
              <Smartphone className="h-10 w-10 text-accent mx-auto mb-2" />
              <div className="font-heading font-bold text-lg text-primary">{t('pwa_ios_title')}</div>
              <div className="text-sm text-muted-foreground mt-1">{t('pwa_ios_desc')}</div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-xl">
                <div className="w-8 h-8 bg-accent text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
                <div className="text-sm">{t('pwa_ios_step1')}</div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-xl">
                <div className="w-8 h-8 bg-accent text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
                <div className="text-sm">{t('pwa_ios_step2')}</div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-xl">
                <div className="w-8 h-8 bg-accent text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
                <div className="text-sm">{t('pwa_ios_step3')}</div>
              </div>
            </div>
            <Button className="w-full rounded-full" onClick={() => setShowIOSGuide(false)}>{t('pwa_ios_close')}</Button>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-border bg-secondary/30 py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="font-logo text-2xl font-bold text-primary mb-4 flex items-center gap-2">
                <img src={logoUrl} alt="PRC Remit" className="h-8 w-8 rounded-full object-cover" />
                PRC Remit
              </div>
              <p className="text-sm text-muted-foreground max-w-sm">{t('landing_footer_desc')}</p>
            </div>
            <div>
              <h4 className="font-semibold text-primary mb-4">{t('landing_footer_service')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/transfer-methods" className="hover:text-primary smooth-transition">{t('landing_footer_transfer_methods')}</Link></li>
                <li><Link to="/pricing" className="hover:text-primary smooth-transition">{t('landing_footer_pricing')}</Link></li>
                <li><a href="/docs/index.html" target="_blank" rel="noopener noreferrer" className="hover:text-primary smooth-transition">{t('landing_footer_docs')}</a></li>
                <li><Link to="/faq" className="hover:text-primary smooth-transition">FAQ</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-primary mb-4">{t('landing_footer_company')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/company" className="hover:text-primary smooth-transition">{t('landing_footer_about')}</Link></li>
                <li><Link to="/terms" className="hover:text-primary smooth-transition">{t('landing_footer_terms')}</Link></li>
                <li><Link to="/privacy" className="hover:text-primary smooth-transition">{t('landing_footer_privacy')}</Link></li>
                <li><Link to="/login" className="hover:text-primary smooth-transition">{t('landing_footer_admin')}</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border pt-8 text-center text-sm text-muted-foreground">
            <p>{footerText || '\u00a9 2026 PRC Remit. All rights reserved.'}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
